/* eslint-disable max-lines */
import React, { createRef, useEffect, useRef, useState } from 'react';
import { CFToastContainer } from '../../components';
import { CFToastProviderContext } from './ToastProviderContext';
export const TOAST_PROVIDER_DEFAULT_PRIORITY = -1;
export const CFToastProvider = ({ bottomOffset = 0, children, onCreateToast, onCancelToast, onDismissToast, onRemoveToast, onStartToast, onStopToast, onTimeoutToast, }) => {
    const [toastUpdated, updateToasts] = useState(0);
    const toasts = useRef([]);
    //
    // Adding a new toast involves transferring the toast configurations
    // to our toasts queue including adding a ref with which we can call Toast handles
    // such as `start()` and `stop()`
    //
    const addToastToQueue = ({ priority = TOAST_PROVIDER_DEFAULT_PRIORITY, props, }) => {
        const newToast = {
            id: props.id,
            priority,
            props: Object.assign(Object.assign({ startOncreate: false }, props), { onCancel: (id) => {
                    if (props.onCancel)
                        props.onCancel(id);
                    if (onCancelToast)
                        onCancelToast(id);
                }, onCreate: (id) => {
                    if (props.onCreate)
                        props.onCreate(id);
                    if (onCreateToast)
                        onCreateToast(id, priority);
                }, onDismiss: (id) => {
                    if (props.onDismiss)
                        props.onDismiss(id);
                    if (onDismissToast)
                        onDismissToast(id);
                }, onRemove: (id, payload) => {
                    removeToastFromQueue(id);
                    if (props.onRemove)
                        props.onRemove(id, payload);
                    if (onRemoveToast)
                        onRemoveToast(id, payload);
                }, onStart: (id) => {
                    if (props.onStart)
                        props.onStart(id);
                    if (onStartToast)
                        onStartToast(id);
                }, onStop: (id) => {
                    if (props.onStop)
                        props.onStop(id);
                    if (onStopToast)
                        onStopToast(id);
                }, onTimeout: (id) => {
                    if (props.onTimeout)
                        props.onTimeout(id);
                    if (onTimeoutToast)
                        onTimeoutToast(id);
                } }),
            ref: createRef(),
        };
        let newToasts = [];
        const currentToasts = toasts.current;
        switch (priority) {
            case 1:
                newToasts = [newToast, ...currentToasts];
                break;
            case 0:
                {
                    const topToast = currentToasts.shift();
                    if (topToast) {
                        newToasts = [topToast, newToast, ...currentToasts];
                    }
                    else {
                        newToasts = [newToast, ...currentToasts];
                    }
                }
                break;
            case -1:
                newToasts = [...currentToasts, newToast];
                break;
        }
        toasts.current = newToasts;
        updateToasts(toastUpdated + 1);
    };
    //
    // Instructing a toast to cancel begins its internal cancel cycle, which leads to its internal `remove()` method.
    // So this hook just catches this request to cancel a toast and kicks off the process.
    // The `removeToast()` hook takes care of adjusting the queue at the appropriate point in the process.
    //
    const cancelToast = (toastID) => {
        var _a;
        const match = toasts.current.find(({ id }) => id === toastID);
        if (match) {
            // NOTE: cancel() will trigger the toast to remove itself from the queue
            ((_a = match.ref) === null || _a === void 0 ? void 0 : _a.current).cancel();
        }
    };
    //
    // Toasts in `toasts` only visually hide when they internall `remove()`
    // But we must signal to the toastQueue that a toast has indeed been removed.
    //
    const removeToastFromQueue = (toastID) => {
        toasts.current = toasts.current.filter(({ id }) => id !== toastID);
        updateToasts((val) => val + 1);
    };
    //
    // When a toast is removed we need to `play()` the next item in the queue.
    // Since toasts are stored using a ref we need to watch the toastUpdated instead.
    // When it changes we just ensure the current top item in the queue is started and others are stopped.
    //
    useEffect(() => {
        toasts.current.forEach(({ ref }, index) => {
            if (!ref || !ref.current) {
                return;
            }
            if (index === 0) {
                ref.current.start();
                return;
            }
            ref.current.stop();
        });
    }, [toastUpdated]);
    return (React.createElement(CFToastProviderContext.Provider, { value: {
            addToast: addToastToQueue,
            cancelToast,
        } },
        children,
        React.createElement(CFToastContainer, { bottomOffset: bottomOffset, queue: toasts.current.map(({ props, ref, id }) => (Object.assign(Object.assign({ ref }, props), { id }))) })));
};
